import type { ReactNode } from 'react'
import { createContext, useContext } from 'react'
import { observable, runInAction } from 'mobx'
import type { IObservableValue, ObservableMap } from 'mobx'

type Callback = () => void | boolean | Promise<void | boolean>
type LabelOrComponent = string | ReactNode
type RegisterOptions = {
  // Tools that share a group render in a sub-panel behind one collapsible row
  // instead of in the main list. A group exists while any tool names it.
  group?: string
}
export type QATool = {
  label: LabelOrComponent
  callback: Callback
  group?: string
}
type QAToolsContextType = {
  enabled: IObservableValue<boolean>
  hide: () => void
  show: () => void
  toggle: () => void
  tools: ObservableMap<string, QATool>
  // The group whose sub-panel is open, if any. Kept here rather than in
  // React state so it survives the pane hiding after a tool click.
  expandedGroup: IObservableValue<string | null>
  toggleGroup: (group: string) => void
  register: (
    name: string,
    label: LabelOrComponent,
    callback?: Callback,
    options?: RegisterOptions
  ) => void
  unregister: (name: string) => void
  changeLabel: (name: string, label: string) => void
}

export const QAToolsContext = createContext<QAToolsContextType | null>(null)

const noopTools: QAToolsContextType = {
  enabled: observable.box(false),
  tools: observable.map(),
  expandedGroup: observable.box<string | null>(null),
  toggleGroup: () => {},
  register: () => {},
  unregister: () => {},
  changeLabel: () => {},
  show: () => {}, // No-op
  hide: () => {}, // No-op
  toggle: () => {}, // No-op,
}

export const activeTools: QAToolsContextType = {
  enabled: observable.box(false),
  tools: observable.map<string, QATool>(),
  expandedGroup: observable.box<string | null>(null),
  register(
    name: string,
    label: LabelOrComponent,
    callback?: Callback,
    options?: RegisterOptions
  ) {
    runInAction(() => {
      this.tools.set(name, {
        label,
        callback: callback || (() => {}),
        group: options?.group,
      })
    })
  },
  unregister(name: string) {
    runInAction(() => {
      const group = this.tools.get(name)?.group
      this.tools.delete(name)
      // Collapse the sub-panel when its last tool goes away, so re-registering
      // the group later does not pop it open without a click.
      if (group && group === this.expandedGroup.get()) {
        const stillPresent = Array.from(this.tools.values()).some(
          (tool) => tool.group === group
        )
        if (!stillPresent) this.expandedGroup.set(null)
      }
    })
  },
  changeLabel(name: string, label: LabelOrComponent) {
    const tool = this.tools.get(name)
    if (tool) {
      const newTool = { ...tool, label }
      runInAction(() => {
        this.tools.set(name, newTool)
      })
    }
  },
  toggleGroup(group: string) {
    runInAction(() => {
      this.expandedGroup.set(this.expandedGroup.get() === group ? null : group)
    })
  },
  show() {
    runInAction(() => {
      this.enabled.set(true)
    })
  },
  hide() {
    runInAction(() => {
      this.enabled.set(false)
    })
  },
  toggle() {
    runInAction(() => {
      this.enabled.set(!this.enabled.get())
    })
  },
}

export function useQATools() {
  const context = useContext(QAToolsContext)

  if (!context) {
    return noopTools
  }
  return context
}
